import { useCallback, useEffect, useState } from 'react';
import { fetchGetUserTagsDirect } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';

export const normalizeTag = (tag: string) => tag.trim().toLowerCase();

const sortTags = (tags: string[]) =>
  [...new Set(tags.map(normalizeTag))].filter(Boolean).sort((a, b) => a.localeCompare(b));

export interface UseSecondBrainTagsReturn {
  availableTags: string[];
  selectedTags: string[];
  isLoadingTags: boolean;
  setSelectedTags: (tags: string[]) => void;
  loadTags: () => Promise<void>;
  clearSelectedTags: () => void;
}

export const useSecondBrainTags = (): UseSecondBrainTagsReturn => {
  const { userToken } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);

  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  const loadTags = useCallback(async () => {
    if (!userToken) {
      return;
    }

    setIsLoadingTags(true);
    try {
      const tags = await fetchGetUserTagsDirect({
        headers: { authorization: `Bearer ${userToken}` },
      });
      if (Array.isArray(tags)) {
        setAvailableTags(sortTags(tags.map(String)));
      } else {
        setAvailableTags([]);
      }
    } catch (error) {
      errorReporter.expected('Failed to load user tags for Second Brain', error, {
        feature: 'secondBrain',
        operation: 'loadTags',
      });
      showAlert('We could not load your tags. Please try again.', 'error');
    } finally {
      setIsLoadingTags(false);
    }
  }, [showAlert, userToken]);

  const clearSelectedTags = useCallback(() => {
    setSelectedTags([]);
  }, []);

  useEffect(() => {
    void loadTags();
  }, [loadTags]);

  return {
    availableTags,
    selectedTags,
    isLoadingTags,
    setSelectedTags,
    loadTags,
    clearSelectedTags,
  };
};
